import { useCallback } from 'react';

import { useTranslate } from '@growtherapy/i18n';

import { RejoinModalViewModel } from '../../session/rejoin/types';
import {
  REJOIN_ERROR_TOAST_TAGS,
  RejoinError,
  useBaseRejoinModalViewModel,
} from '../../session/rejoin/useBaseRejoinModalViewModel';
import { toast } from '../../toasts';
import { useProviderDisconnectRoomCallback } from '../useProviderDisconnectRoomCallback';

function errorToToastMessage(error: RejoinError) {
  const errorKind = 'kind' in error ? error.kind : 'unexpected';
  switch (errorKind) {
    case 'empty-room':
      return 'Your patient may have left the session. Please re-admit the patient.';
    default:
      return 'An unexpected error occurred. Please re-admit the patient.';
  }
}

function showErrorToast(error: RejoinError) {
  toast.error(errorToToastMessage(error), {
    purpose: 'system',
    tags: REJOIN_ERROR_TOAST_TAGS,
  });
}

export function useProviderRejoinModalViewModel(): RejoinModalViewModel {
  const t = useTranslate();
  const { isLoading, isOpen, rejoin, closeModal } = useBaseRejoinModalViewModel();
  const disconnect = useProviderDisconnectRoomCallback();

  const handleRejoin = useCallback(() => {
    rejoin({
      t,
      onTerminalFailure: async (error) => {
        return disconnect().finally(() => {
          showErrorToast(error);
          closeModal();
        });
      },
    });
  }, [t, closeModal, disconnect, rejoin]);

  return {
    isOpen,
    isLoading,
    onJoin: handleRejoin,
  };
}
